/*  TestimonialsTab — Admin CRUD for the Testimonials collection.
    ────────────────────────────────────────────────────────────────
    iter100.150 — Marketing manages homepage Real Stories + every
    product page's testimonials from ONE table (no JSON editing).

    Endpoints:
        GET    /api/admin/testimonials
        POST   /api/admin/testimonials
        PATCH  /api/admin/testimonials/{id}
        DELETE /api/admin/testimonials/{id}
        POST   /api/admin/media/upload   (used for photo picker)
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Plus, PencilSimple, Trash, Star, ArrowsClockwise, CheckCircle,
    XCircle, ImageSquare, UploadSimple, X, CheckSquareOffset,
} from "@phosphor-icons/react";

import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";


const SCOPE_OPTIONS = [
    { value: "home",             label: "Homepage",           short: "Home" },
    { value: "product_secure",   label: "Night Guard",         short: "Night" },
    { value: "product_serene",   label: "Anti-Snoring Guard",  short: "Serene" },
    { value: "product_apex",     label: "Sports Guard",        short: "Apex" },
    { value: "product_infinite", label: "Retainer",            short: "Retainer" },
];


const EMPTY_FORM = {
    id: null,
    name: "",
    quote: "",
    image_url: "",
    city: "",
    case_label: "",
    scopes: [],
    rating: 5,
    verified: true,
    display_order: 100,
    is_published: true,
};


export default function TestimonialsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [filterScope, setFilterScope] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/testimonials");
            setItems(data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load testimonials.");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (filterScope === "all") return items;
        return items.filter((it) => (it.scopes || []).includes(filterScope));
    }, [items, filterScope]);

    const scopeCounts = useMemo(() => {
        const c = { all: items.length };
        for (const s of SCOPE_OPTIONS) c[s.value] = 0;
        for (const it of items) for (const s of it.scopes || []) c[s] = (c[s] || 0) + 1;
        return c;
    }, [items]);

    const openNew = () => { setForm({ ...EMPTY_FORM, scopes: filterScope === "all" ? [] : [filterScope] }); setShowForm(true); };
    const openEdit = (it) => { setForm({ ...EMPTY_FORM, ...it }); setShowForm(true); };
    const closeForm = () => { setShowForm(false); setForm(EMPTY_FORM); };

    const save = async () => {
        if (!form.name.trim() || !form.quote.trim()) {
            toast.error("Name and quote are required.");
            return;
        }
        if (form.scopes.length === 0) {
            toast.error("Pick at least one page where this testimonial should appear.");
            return;
        }
        setSaving(true);
        try {
            const body = {
                name: form.name.trim(),
                quote: form.quote.trim(),
                image_url: form.image_url,
                city: form.city,
                case_label: form.case_label,
                scopes: form.scopes,
                rating: form.rating,
                verified: form.verified,
                display_order: form.display_order,
                is_published: form.is_published,
            };
            if (form.id) {
                await api.patch(`/admin/testimonials/${form.id}`, body);
                toast.success("Testimonial saved.");
            } else {
                await api.post("/admin/testimonials", body);
                toast.success("Testimonial created.");
            }
            closeForm();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally { setSaving(false); }
    };

    const remove = async (it) => {
        if (!window.confirm(`Delete testimonial by "${it.name}"? This can't be undone.`)) return;
        try {
            await api.delete(`/admin/testimonials/${it.id}`);
            toast.success("Deleted.");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete.");
        }
    };

    const togglePublished = async (it) => {
        try {
            await api.patch(`/admin/testimonials/${it.id}`, { ...it, is_published: !it.is_published });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't toggle publish.");
        }
    };

    const handlePhotoUpload = async (file, event) => {
        // iter100.208 — reset input after handling so re-uploading the
        // same file (or any file after the first one) re-triggers
        // `onChange`. Without this, subsequent uploads silently fail
        // because the input's internal value hasn't changed.
        if (event?.target) event.target.value = "";
        if (!file) return;
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("category", "testimonial");
            fd.append("alt", form.name || "Patient portrait");
            const { data } = await api.post("/admin/media/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, image_url: data.url }));
            toast.success("Photo uploaded.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed.");
        }
    };

    return (
        <div className="space-y-4" data-testid="testimonials-tab">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Testimonials</div>
                    <h2 className="font-display text-lg font-bold text-foreground mt-0.5">
                        Real Stories · Product testimonials
                    </h2>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Every patient story that appears on the marketing site. Assign each to one or more pages via <b>Scopes</b>.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="testimonials-refresh">
                        <ArrowsClockwise size={11} weight="bold" className="mr-1" /> Refresh
                    </Button>
                    <Button size="sm" onClick={openNew} data-testid="testimonials-new">
                        <Plus size={11} weight="bold" className="mr-1" /> Add testimonial
                    </Button>
                </div>
            </div>

            {/* iter100.228 — Cross-link to the Approvals command center where
                DOCTOR-authored testimonials + case studies await editorial
                approval. Marketing (this page) creates admin-authored ones;
                Approvals handles the doctor-authored side of the same pipeline. */}
            <Link
                to="/admin/approvals?tab=content"
                className="inline-flex items-center gap-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs text-primary hover:bg-primary/10 transition"
                data-testid="testimonials-cross-link-approvals"
            >
                <CheckSquareOffset size={14} weight="duotone" />
                <span>
                    Reviewing <span className="font-semibold">doctor-authored</span> testimonials & case studies?
                    Head to the <span className="font-semibold underline">Approvals</span> center.
                </span>
            </Link>

            {/* Scope filter chips */}
            <div className="flex flex-wrap gap-2" data-testid="testimonials-filter">
                <ScopeChip label={`All (${scopeCounts.all})`} active={filterScope === "all"} onClick={() => setFilterScope("all")} testid="filter-scope-all" />
                {SCOPE_OPTIONS.map((s) => (
                    <ScopeChip
                        key={s.value}
                        label={`${s.label} (${scopeCounts[s.value] || 0})`}
                        active={filterScope === s.value}
                        onClick={() => setFilterScope(s.value)}
                        testid={`filter-scope-${s.value}`}
                    />
                ))}
            </div>

            {/* List */}
            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="text-sm text-muted-foreground py-10 text-center bg-secondary/20 rounded-2xl">
                    No testimonials in this scope yet. Click <b>Add testimonial</b> to create one.
                </div>
            ) : (
                <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials-list">
                    {filtered.map((it) => (
                        <li key={it.id} className={`bg-card border rounded-2xl overflow-hidden ${it.is_published ? "border-border" : "border-dashed border-amber-300 bg-amber-50/30"}`} data-testid={`testimonial-card-${it.id}`}>
                            {it.image_url ? (
                                <div className="aspect-[4/5] bg-secondary/40 relative overflow-hidden">
                                    <img src={it.image_url} alt={it.name} className="absolute inset-0 w-full h-full object-cover" />
                                    <div className="absolute top-2 left-2 flex gap-1 flex-wrap max-w-[calc(100%-16px)]">
                                        {(it.scopes || []).map((s) => (
                                            <span key={s} className="px-1.5 py-0.5 rounded bg-white/95 backdrop-blur text-[9px] font-bold uppercase tracking-wider text-foreground">
                                                {SCOPE_OPTIONS.find((o) => o.value === s)?.short || s}
                                            </span>
                                        ))}
                                    </div>
                                    {!it.is_published && (
                                        <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-amber-500 text-white text-[9px] font-bold uppercase tracking-wider">Draft</div>
                                    )}
                                </div>
                            ) : (
                                <div className="aspect-[4/5] bg-secondary/40 grid place-items-center">
                                    <ImageSquare size={40} className="text-muted-foreground/40" />
                                </div>
                            )}
                            <div className="p-3">
                                <div className="text-sm font-semibold text-foreground line-clamp-1">{it.name}</div>
                                <div className="text-[10.5px] text-muted-foreground mt-0.5">
                                    {it.city}{it.city && it.case_label ? " · " : ""}{it.case_label}
                                </div>
                                <div className="text-xs text-foreground/80 mt-2 line-clamp-3 leading-relaxed">
                                    &ldquo;{it.quote}&rdquo;
                                </div>
                                <div className="mt-3 pt-2 border-t border-border/60 flex items-center justify-between gap-1">
                                    <div className="flex items-center gap-0.5 text-amber-500">
                                        {Array.from({ length: it.rating }).map((_, i) => <Star key={i} size={10} weight="fill" />)}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => togglePublished(it)} className="text-[10px] px-1.5 py-0.5 rounded border border-border hover:border-primary hover:text-primary transition-colors" title={it.is_published ? "Unpublish" : "Publish"} data-testid={`testimonial-toggle-published-${it.id}`}>
                                            {it.is_published ? <CheckCircle size={12} weight="fill" className="text-emerald-500 inline" /> : <XCircle size={12} weight="fill" className="text-muted-foreground inline" />}
                                        </button>
                                        <button onClick={() => openEdit(it)} className="p-1 rounded border border-border hover:border-primary hover:text-primary transition-colors" title="Edit" data-testid={`testimonial-edit-${it.id}`}>
                                            <PencilSimple size={11} weight="bold" />
                                        </button>
                                        <button onClick={() => remove(it)} className="p-1 rounded border border-border hover:border-rose-400 hover:text-rose-600 transition-colors" title="Delete" data-testid={`testimonial-delete-${it.id}`}>
                                            <Trash size={11} weight="bold" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {/* Form modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center p-4" onClick={closeForm}>
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="testimonial-form-modal">
                        <div className="sticky top-0 bg-white border-b border-border px-5 py-3 flex items-center justify-between">
                            <div className="font-display text-lg font-bold">{form.id ? "Edit testimonial" : "New testimonial"}</div>
                            <button onClick={closeForm} className="p-1 rounded hover:bg-secondary" data-testid="testimonial-form-close">
                                <X size={16} weight="bold" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            {/* Photo picker */}
                            <div>
                                <div className="text-xs font-semibold text-foreground mb-1.5">Patient photo</div>
                                <div className="flex items-center gap-3">
                                    <div className="w-24 aspect-[4/5] bg-secondary/40 rounded-lg overflow-hidden border border-border">
                                        {form.image_url ? (
                                            <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full grid place-items-center"><ImageSquare size={24} className="text-muted-foreground/40" /></div>
                                        )}
                                    </div>
                                    <div className="flex-1 space-y-2">
                                        <Input
                                            type="text"
                                            value={form.image_url}
                                            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                            placeholder="/marketing/P1.png or paste URL"
                                            className="text-xs"
                                            data-testid="testimonial-form-image-url"
                                        />
                                        <label className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border bg-white text-xs font-semibold cursor-pointer hover:border-primary hover:text-primary transition-colors">
                                            <UploadSimple size={11} weight="bold" />
                                            Upload photo
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => handlePhotoUpload(e.target.files?.[0], e)}
                                                data-testid="testimonial-form-upload"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <div className="text-xs font-semibold text-foreground mb-1.5">Patient name *</div>
                                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rachana" data-testid="testimonial-form-name" />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-foreground mb-1.5">City</div>
                                    <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Bengaluru" data-testid="testimonial-form-city" />
                                </div>
                            </div>

                            <div>
                                <div className="text-xs font-semibold text-foreground mb-1.5">Quote *</div>
                                <Textarea rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} placeholder="The doctor was so honest..." data-testid="testimonial-form-quote" />
                            </div>

                            <div>
                                <div className="text-xs font-semibold text-foreground mb-1.5">Case metadata</div>
                                <Input value={form.case_label} onChange={(e) => setForm({ ...form, case_label: e.target.value })} placeholder="Crowding · 9 months" data-testid="testimonial-form-case" />
                                <div className="text-[10.5px] text-muted-foreground mt-1">Shown below the name. Use a bullet {" · "} to separate case type and duration.</div>
                            </div>

                            <div>
                                <div className="text-xs font-semibold text-foreground mb-1.5">Show on these pages *</div>
                                <div className="flex flex-wrap gap-2" data-testid="testimonial-form-scopes">
                                    {SCOPE_OPTIONS.map((s) => (
                                        <label key={s.value} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border cursor-pointer text-xs transition-colors ${form.scopes.includes(s.value) ? "bg-primary text-white border-primary" : "bg-white border-border hover:border-primary/50"}`}>
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={form.scopes.includes(s.value)}
                                                onChange={(e) => {
                                                    const set = new Set(form.scopes);
                                                    if (e.target.checked) set.add(s.value); else set.delete(s.value);
                                                    setForm({ ...form, scopes: Array.from(set) });
                                                }}
                                                data-testid={`testimonial-form-scope-${s.value}`}
                                            />
                                            <span>{s.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-3 gap-3">
                                <div>
                                    <div className="text-xs font-semibold text-foreground mb-1.5">Rating</div>
                                    <Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value || "5", 10) })} data-testid="testimonial-form-rating" />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-foreground mb-1.5">Sort order</div>
                                    <Input type="number" min="0" max="10000" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || "100", 10) })} data-testid="testimonial-form-order" />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-foreground mb-1.5">Publish</div>
                                    <div className="flex items-center gap-2 h-9">
                                        <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="testimonial-form-published" />
                                        <span className="text-xs text-muted-foreground">{form.is_published ? "Live" : "Draft"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-white border-t border-border px-5 py-3 flex items-center justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={closeForm} disabled={saving}>Cancel</Button>
                            <Button size="sm" onClick={save} disabled={saving} data-testid="testimonial-form-save">
                                {saving ? "Saving…" : (form.id ? "Save changes" : "Create testimonial")}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function ScopeChip({ label, active, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                active ? "bg-primary border-primary text-white" : "bg-card border-border text-muted-foreground hover:bg-secondary"
            }`}
            data-testid={testid}
        >
            {label}
        </button>
    );
}
